import { ReactElement, useEffect, useId, useRef, useState } from "react";
import Close from "../../components/Atomos/Icons/Close";
import FileInputIcon from "../../components/Atomos/Icons/FileInputIcon";
import Modal from "../../components/Atomos/Modals/Modal";
import Button from "../../components/staff/Button";
import { toastError } from "../../utils/toast";

const FOOTER_BUTTON_CLASS = "w-full justify-center sm:!w-auto";
const FEED_MAX_SIZE_MB = 25;

export type UploadFeedPayload = {
  feedData: string;
  fileName: string;
};

type UploadFeedModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (payload: UploadFeedPayload) => Promise<void>;
};

function isXmlFile(file: File): boolean {
  const name = file.name.toLowerCase();
  return name.endsWith(".xml") || file.type === "text/xml" || file.type === "application/xml";
}

function readFileAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result ?? "");
      const comma = result.indexOf(",");
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(reader.error ?? new Error("Could not read the file."));
    reader.readAsDataURL(file);
  });
}

function fileSizeLabel(bytes: number): string {
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

export default function UploadFeedModal({
  isOpen,
  onClose,
  onConfirm,
}: UploadFeedModalProps): ReactElement {
  const inputId = useId().replace(/:/g, "");
  const inputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      return;
    }
    setFile(null);
    setError(null);
    setDragging(false);
    setSubmitting(false);
    if (inputRef.current) {
      inputRef.current.value = "";
    }
  }, [isOpen]);

  const handleClose = () => {
    if (submitting) {
      return;
    }
    onClose();
  };

  const takeFile = (next: File | undefined) => {
    if (!next || submitting) {
      return;
    }
    if (next.size / (1024 * 1024) > FEED_MAX_SIZE_MB) {
      setError(`File size exceeds ${FEED_MAX_SIZE_MB} MB limit`);
      setFile(null);
      return;
    }
    if (!isXmlFile(next)) {
      setError("Choose an XML file.");
      setFile(null);
      return;
    }
    setError(null);
    setFile(next);
  };

  const handleConfirm = async () => {
    if (!file || submitting) {
      return;
    }
    setSubmitting(true);
    try {
      const feedData = await readFileAsBase64(file);
      await onConfirm({ feedData, fileName: file.name });
    } catch (e: any) {
      toastError(e?.message ?? "Could not read the file.", { autoClose: 6000 });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      isModalOpen={isOpen}
      toggleModal={handleClose}
      title="Upload feed"
      titleColor="text-secondary font-semibold text-lg"
      compact
      compactBodyScrolls={false}
      className="w-full max-w-lg border border-gray-200 shadow-lg"
      bodyClassName="!pb-8"
    >
      <div className="flex flex-col gap-5">
        <p className="text-sm leading-relaxed text-[#474F56] sm:text-base">
          Import this firm from an XML file instead of downloading its feed URL.
        </p>

        <div className="flex flex-col gap-1.5">
          <span className="text-base leading-[1.125rem] text-[#BC3323]">
            Feed file
          </span>
          {error && <p className="text-sm text-red-600">{error}</p>}
          <label
            htmlFor={inputId}
            className={`flex min-h-[10rem] cursor-pointer flex-col items-center justify-center rounded-lg border border-dashed px-4 py-6 text-center transition-colors ${
              dragging
                ? "border-accentDefault bg-gray-50"
                : "border-[#474F56] bg-white"
            } ${submitting ? "pointer-events-none opacity-60" : ""}`}
            onDragOver={(event) => {
              event.preventDefault();
              if (!submitting) {
                setDragging(true);
              }
            }}
            onDragLeave={(event) => {
              event.preventDefault();
              setDragging(false);
            }}
            onDrop={(event) => {
              event.preventDefault();
              setDragging(false);
              takeFile(event.dataTransfer.files[0]);
            }}
          >
            <input
              ref={inputRef}
              id={inputId}
              type="file"
              accept=".xml,text/xml,application/xml"
              className="sr-only"
              disabled={submitting}
              onChange={(event) => {
                takeFile(event.target.files?.[0]);
                event.target.value = "";
              }}
            />
            <div className="mb-3 h-10 w-10 fill-[#474F56]">
              <FileInputIcon />
            </div>
            {file ? (
              <>
                <p className="max-w-full truncate text-sm text-neutral-dark" title={file.name}>
                  {file.name}
                  <span className="ml-1 text-gray-500">
                    ({fileSizeLabel(file.size)})
                  </span>
                </p>
                <p className="mt-2 text-sm text-[#474F56]">
                  Drag and drop to replace, or{" "}
                  <span className="font-semibold text-[#2366C2]">choose file</span>
                </p>
              </>
            ) : (
              <p className="text-sm text-[#474F56]">
                Drag and drop your file here, or{" "}
                <span className="font-semibold text-[#2366C2]">choose file</span>
              </p>
            )}
            <p className="mt-2 text-sm text-gray-500">XML only, up to 25 MB.</p>
          </label>
        </div>

        <div className="mt-4 flex w-full min-w-0 flex-col gap-3 sm:flex-row sm:flex-wrap sm:justify-end">
          <Button
            className={FOOTER_BUTTON_CLASS}
            text="Cancel"
            onClick={handleClose}
            color="neutral"
            variant="outlined"
            size="fixed"
            icon={<Close />}
            disabled={submitting}
          />
          <Button
            className={FOOTER_BUTTON_CLASS}
            text={submitting ? "Uploading…" : "Upload"}
            onClick={() => void handleConfirm()}
            color="secondary"
            variant="filled"
            size="fixed"
            disabled={!file || submitting}
          />
        </div>
      </div>
    </Modal>
  );
}
